import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
  isAnyOf,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { normalize } from "normalizr";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { HotelRoom } from "admin/types/hotelRoom";
import { RootState } from "admin/store";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { PageContext } from "admin/types/pageContext";
import { AdminTypes } from "admin/constants/adminTypes";
import {
  fetchHotel,
  fetchHotelIfNeeded,
} from "admin/features/hotel/hotelSlice";
import { objectToFormData } from "admin/utils/objectUtil";
import { hotelSchema } from "admin/schemas/hotelSchema";
import { MasterData } from "admin/types/masterData";
import { HotelRoomStatuses } from "admin/constants/hotelRoomStatuses";

const key = "hotelRoom";
const adapter = createEntityAdapter<HotelRoom.Room>();

const initialStateValues: HotelRoom.HotelRoomState = {
  hotelRoom: null,
  errors: null,
  loading: false,
  showAll: false,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async **/

export const postHotelRoom = createAsyncThunk<
  any,
  { context: PageContext.PageContext; hotelId: string; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postHotelRoom`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/rooms.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/rooms.json`;
    const formData: any = new FormData();
    objectToFormData(params, formData, "hotel_room");
    const result = await createWrapper({ isMultipart: true }).post(
      url,
      formData,
    );
    const normalized = normalize(result.data.hotel, hotelSchema);
    return normalized;
  } catch (err) {
    // reverse proxyで413等のエラーが返されるケースのためにerrorsが無い場合はstatusTextを返す
    return rejectWithValue(err.data.errors || { messages: [err.statusText] });
  }
});

export const putHotelRoom = createAsyncThunk<
  any,
  {
    context: PageContext.PageContext;
    id: number;
    hotelId: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putHotelRoom`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/rooms/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/rooms/${id}.json`;
    const formData: any = new FormData();
    objectToFormData(params, formData, "hotel_room");
    const result = await createWrapper({ isMultipart: true }).put(
      url,
      formData,
    );
    const normalized = normalize(result.data.hotel, hotelSchema);
    return normalized;
  } catch (err) {
    return rejectWithValue(err.data.errors || { messages: [err.statusText] });
  }
});

export const putHotelRoomOrder = createAsyncThunk<
  any,
  {
    context: PageContext.PageContext;
    id: number;
    hotelId: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putHotelRoomOrder`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/rooms/${id}/order.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/rooms/${id}/order.json`;
    const result = await createWrapper().put(url, { hotel_room: params });
    const normalized = normalize(result.data.hotel, hotelSchema);
    return normalized;
  } catch (err) {
    return rejectWithValue(err.data.errors || { messages: [err.statusText] });
  }
});

export const deleteHotelRoomImage = createAsyncThunk<
  { hotelRoomId?: number; id: number },
  { context: PageContext.PageContext; hotelRoomId?: number; id: number },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/deleteHotelRoomImage`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelRoomId, id } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotel_room_images/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotel_room_images/${id}.json`;
    await createWrapper().delete(url);
    return { hotelRoomId: hotelRoomId, id: id };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putHotelRoomImageOrder = createAsyncThunk<
  any,
  {
    context: PageContext.PageContext;
    id: number;
    hotelRoomId: number;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putHotelRoomImageOrder`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelRoomId, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotel_rooms/${hotelRoomId}/images/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotel_rooms/${hotelRoomId}/images/${id}.json`;
    const result = await createWrapper().put(url, { hotel_room_image: params });
    const normalized = normalize(result.data.hotel, hotelSchema);
    return normalized;
  } catch (err) {
    return rejectWithValue(err.data.errors || { messages: [err.statusText] });
  }
});

/** slice **/
export const hotelRoomSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    setShowAll: (state, action) => {
      state.showAll = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.hotelRoom };
      })
      .addCase(postHotelRoom.fulfilled, (state, action) => {
        if (action.payload.entities.hotelRooms) {
          adapter.setAll(state, action.payload.entities.hotelRooms);
        }
      })
      .addCase(putHotelRoom.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.entities.hotelRooms);
      })
      .addCase(putHotelRoomOrder.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.entities.hotelRooms);
      })
      .addCase(deleteHotelRoomImage.fulfilled, (state, action) => {
        const { hotelRoomId, id } = action.payload;
        if (state.entities[hotelRoomId]) {
          state.entities[hotelRoomId].images = state.entities[
            hotelRoomId
          ].images.filter((image) => image.id !== id);
        }
      })
      .addCase(putHotelRoomImageOrder.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.entities.hotelRooms);
      })
      .addMatcher(
        isAnyOf(fetchHotel.fulfilled, fetchHotelIfNeeded.fulfilled),
        (state, action) => {
          adapter.setAll(state, action.payload.entities.hotelRooms ?? []);
        },
      )
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: hotelRoomByIdSelector,
  selectIds: hotelRoomIdsSelector,
  selectEntities: hotelRoomEntitiesSelector,
  selectAll: hotelRoomsSelector,
  selectTotal: totalHotelRoomsSelector,
} = adapter.getSelectors((state: RootState) => state.hotelRoom);

const stateSelector = (state: RootState) => state[key];

function compareByOrder(a, b) {
  const aOrder = a.order || 0;
  const bOrder = b.order || 0;
  if (aOrder > bOrder) return 1;
  if (aOrder < bOrder) return -1;
  return b.id - a.id;
}

export const hotelRoomsByIdsSelector = createSelector(
  [hotelRoomEntitiesSelector, stateSelector, (state, ids: number[]) => ids],
  (entities, state, ids) => {
    if (ids.length === 0) {
      return [];
    }
    const hotelRooms = ids.map((id) => entities[id]).sort(compareByOrder);
    if (state.showAll) {
      return hotelRooms;
    }
    return hotelRooms.filter(
      (hotelRoom) => hotelRoom.status !== HotelRoomStatuses.deleted,
    );
  },
);

export const showAllSelector = createSelector(
  stateSelector,
  (state) => state.showAll,
);

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

/** action export **/
export const { setShowAll } = hotelRoomSlice.actions;

export const convertHotelRoomFormValuesToParams = (
  data: any,
  isNew = false,
) => {
  const params = { ...data };
  if (params.images) {
    params.images = params.images.map((image) => {
      return { value: image };
    });
  }
  if (params.server_images) {
    // 複製による新規作成時は画像URLを持つ場合があるためURLも送信する
    if (isNew) {
      params.remote_image_urls = params.server_images.map((image) => {
        return image.url;
      });
    }
    delete params.server_images;
  }
  if (params.properties) {
    params.properties = params.properties
      .filter((property) => property.selected)
      .map((property) => {
        return {
          room_property_id: property.id,
          important: property.important,
        };
      });
  }
  if (!params.properties || params.properties.length === 0) {
    params.is_empty_properties = true;
  }
  if (!params.amenity_ids || params.amenity_ids.length === 0) {
    params.is_empty_amenity_ids = true;
  }
  if (!params.plan_ids || params.plan_ids.length === 0) {
    params.is_empty_plan_ids = true;
  }
  if (!params.bed_groups || params.bed_groups.length === 0) {
    params.is_empty_bed_groups = true;
  }
  return params;
};

export const convertHotelRoomToFormValues = (
  data: any,
  properties: MasterData.RoomProperty[],
) => {
  const values = { ...data };
  const formProperties =
    properties &&
    properties.map((property) => {
      return {
        id: property.id,
        name: property.name,
        selected: false,
        important: false,
      };
    });
  const selectedProperties =
    values.properties &&
    values.properties.reduce((acc, cur) => {
      acc[cur.roomPropertyId] = cur;
      return acc;
    }, {});
  const formValueProperties =
    formProperties &&
    formProperties.map((propertyData) => {
      if (selectedProperties[propertyData.id]) {
        const selectedProperty = selectedProperties[propertyData.id];
        // nameだけpropertyData(MasterData)から取得する
        return {
          id: propertyData.id,
          name: propertyData.name,
          selected: true,
          important: selectedProperty.important,
        };
      } else {
        return propertyData;
      }
    });
  if (formValueProperties && formValueProperties.length > 0) {
    values.properties = formValueProperties;
  }
  if (values.amenities) {
    values.amenity_ids = values.amenities.map((amenity) =>
      String(amenity.roomAmenityId),
    );
    delete values.amenities;
  }
  if (values.planIds) {
    values.plan_ids = values.planIds.map((planId) => String(planId));
    delete values.planIds;
  }
  // すでに設定されているimagesはリクエストに含めないため別名で保持する
  if (values.images) {
    // orderでソートする
    const images = [...values.images];
    images.sort((a, b) => a.order - b.order);
    values.server_images = images;
    delete values.images;
  }
  return values;
};
